import React from 'react';
import { Calendar, SlidersHorizontal } from 'lucide-react';
import { NotificationBell } from '../notifications/NotificationBell';
import { StatusCounts } from './dashboardModel';

interface Props {
    greeting: string;
    firstName: string;
    /** „Freitag, 25. September" */
    dateStr: string;
    kw: number;
    timeStr: string;
    stats: StatusCounts;
    unread: number;
    onBell: () => void;
    mobile?: boolean;
    /** „Anpassen": Dashboard-Bausteine anordnen. Ohne Handler kein Knopf. */
    onCustomize?: () => void;
    /** Anpassen-Modus laeuft — der Knopf zeigt es als gedrueckt. */
    customizing?: boolean;
}

/** Kennzahl-Chip: die Zahl traegt das Gewicht, das Wort erklaert sie nur. */
const CHIP_TONE = {
    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    blue: 'bg-blue-50 text-blue-800 border-blue-200',
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
    orange: 'bg-orange-50 text-orange-800 border-orange-200',
} as const;

const StatChip: React.FC<{ value: number; label: string; tone: keyof typeof CHIP_TONE }> = ({ value, label, tone }) => (
    <span className={`inline-flex items-baseline gap-1 pl-2 pr-2.5 py-1 rounded-lg border ${CHIP_TONE[tone]}`}>
        <span className="text-[13px] font-semibold leading-none tabular">{value}</span>
        <span className="text-[11px] font-medium uppercase tracking-wide opacity-80">{label}</span>
    </span>
);

const buildStamp = (): string => {
    try {
        return `v${__APP_VERSION__} · Build ${new Date(__BUILD_TIME__).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
    } catch {
        return '';
    }
};

/**
 * Kopfzeile: Gruss, Datum mit Kalenderwoche, Glocke, Kennzahl-Chips und der
 * Build-Stempel (zum Abgleich, ob alle dieselbe Version nutzen).
 */
export const DashboardHeader: React.FC<Props> = ({ greeting, firstName, dateStr, kw, timeStr, stats, unread, onBell, mobile = false, onCustomize, customizing = false }) => {
    if (mobile) {
        return (
            <header className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <h1 data-tutorial="dashboard.begruessung" className="text-lg font-semibold text-slate-900 truncate">{greeting}, {firstName}!</h1>
                    <p className="text-[11px] text-slate-500 mt-0.5">{dateStr} · <span className="tabular">KW {kw}</span></p>
                </div>
                <div className="flex items-center shrink-0 -mr-2 -mt-1">
                    {onCustomize && (
                        <button
                            type="button"
                            onClick={onCustomize}
                            aria-label="Dashboard anpassen"
                            title="Dashboard anpassen"
                            className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-800 active:bg-slate-200 transition-colors"
                        >
                            <SlidersHorizontal className="w-5 h-5" />
                        </button>
                    )}
                    <NotificationBell count={unread} onClick={onBell} size="lg" />
                </div>
            </header>
        );
    }

    const stamp = buildStamp();
    return (
        <header className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
                <h1 data-tutorial="dashboard.begruessung" className="text-base font-semibold text-slate-900 tracking-tight truncate">{greeting}, {firstName}!</h1>
                <p className="text-[11px] text-slate-500 flex items-center gap-1 flex-wrap">
                    <Calendar className="w-3 h-3 shrink-0" /> {dateStr} · <span className="tabular">KW {kw}</span> · <span className="tabular">{timeStr}</span>
                    {stamp && (
                        <span className="ml-1.5 text-[11px] font-mono text-slate-400" title="App-Version (Build-Zeitpunkt) — zum Abgleich, ob alle dieselbe Version nutzen">
                            {stamp}
                        </span>
                    )}
                </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <div data-tutorial="dashboard.kennzahlen" className="flex items-center gap-1.5">
                    <StatChip value={stats.active} label="Aktiv" tone="emerald" />
                    <StatChip value={stats.planning} label="Planung" tone="blue" />
                    {stats.subs > 0 && <StatChip value={stats.subs} label="Vertretung" tone="teal" />}
                    {stats.onHold > 0 && <StatChip value={stats.onHold} label="Ruht" tone="orange" />}
                </div>
                {onCustomize && (
                    <button
                        type="button"
                        onClick={onCustomize}
                        aria-pressed={customizing}
                        title="Dashboard anpassen: Bausteine anordnen, ein- und ausblenden"
                        className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[12px] font-semibold transition-colors ${customizing ? 'border-blue-700 bg-blue-700 text-white hover:bg-blue-800' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:text-slate-900'}`}
                    >
                        <SlidersHorizontal className="w-4 h-4 shrink-0" aria-hidden="true" />
                        Anpassen
                    </button>
                )}
                <NotificationBell count={unread} onClick={onBell} />
            </div>
        </header>
    );
};
